import { modeAwareStorage } from "@/lib/managers/ModeAwareStorageManager";
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import { toast } from "@/hooks/use-toast";

/**
 * Clears all game data except daily streak count
 * Used for testing purposes when ?clear=true is in URL
 */
export const clearAllGameData = () => {
  console.log('Clearing all game data except daily streak...');

  try {
    // Backup only the daily streak count
    const dailyStreak = directCookieAccess.getCookie('babblegram_daily_streak');
    console.log('Backed up daily streak:', dailyStreak);

    // Clear all mode-aware storage data
    const originalMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    
    // Clear daily mode data
    localStorage.setItem('babblegram_game_mode', 'daily');
    modeAwareStorage.clearModeState();
    
    // Clear practice mode data
    localStorage.setItem('babblegram_game_mode', 'practice');
    modeAwareStorage.clearModeState();
    
    // Restore original mode
    localStorage.setItem('babblegram_game_mode', originalMode);
    
    // Clear cross-mode keys
    modeAwareStorage.clearCrossModeKeys();
    
    // Clear all cookies that might exist (iterate through known prefixes)
    const cookiePrefixes = ['babblegram_', 'babblegram_practice_', 'babblegram_daily_'];
    const knownKeys = [
      'current_level',
      'current_word', 
      'saved_guesses',
      'keyboard_status',
      'game_status',
      'previous_word',
      'persistence_date',
      'last_played',
      'streak',
      'max_streak',
      'last_win_date',
      'failed_level',
      'from_welcome_page',
      'redirect_to_welcome',
      'game_persistence_date',
      'guess_history',
      'level_attempts',
      'current_guess',
      'is_transitioning',
      'completed_levels',
      'game_completion_state',
      'immediate_completion',
      'in_progress_overlay_shown',
      'instructions_shown',
      'has_played_before',
      'previous_streak'
    ];
    
    cookiePrefixes.forEach(prefix => {
      knownKeys.forEach(key => {
        directCookieAccess.deleteCookie(`${prefix}${key}`);
      });
    });
    
    // Clear general cookies
    const generalCookies = ['redirect_to_welcome', 'babblegram_test_cookie'];
    generalCookies.forEach(cookie => {
      directCookieAccess.deleteCookie(cookie);
    });
    
    // Clear legacy cookies (pre-mode-aware system)
    // These would have been created before we had daily/practice prefixes
    const legacyCookieNames = [
      'babblegram_current_level',
      'babblegram_current_word', 
      'babblegram_saved_guesses',
      'babblegram_keyboard_status',
      'babblegram_game_status',
      'babblegram_previous_word',
      'babblegram_persistence_date',
      'babblegram_last_played',
      'babblegram_streak',
      'babblegram_max_streak',
      'babblegram_last_win_date',
      'babblegram_failed_level',
      'babblegram_from_welcome_page',
      'babblegram_redirect_to_welcome',
      'babblegram_game_persistence_date',
      'babblegram_guess_history',
      'babblegram_level_attempts',
      'babblegram_current_guess',
      'babblegram_is_transitioning',
      'babblegram_completed_levels',
      'babblegram_game_completion_state',
      'babblegram_immediate_completion',
      'babblegram_in_progress_overlay_shown',
      'babblegram_instructions_shown',
      'babblegram_has_played_before',
      'babblegram_previous_streak'
    ];
    
    legacyCookieNames.forEach(cookieName => {
      directCookieAccess.deleteCookie(cookieName);
    });
    
    // Clear localStorage items
    const localStorageKeys = [
      'babblegram_game_mode',
      'babblegram_page_loaded',
      'from_welcome_page',
      'shared_sequence_processed',
      'babblegram_user_seed',
      'babblegram_practice_mode_play_again',
      'babblegram_practice_mode_date',
      'babblegram_practice_streak',
      'babblegram_practice_max_streak',
      'babblegram_mode_switch_in_progress',
      'babblegram_force_mode_change',
      'babblegram_force_mode',
      'babblegram_previous_mode',
      'babblegram_next_mode',
      'babblegram_current_sequence',
      'babblegram_level_state',
      'babblegram_practice_sequence',
      'babblegram_practice_mode_use_shared_sequence',
      'babblegram_practice_mode_force_reset',
      'babblegram_force_game_reset',
      'babblegram_strategy_reset',
      'babblegram_play_again'
    ];
    
    localStorageKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear all localStorage items that start with our prefixes
    const allLocalStorageKeys = Object.keys(localStorage);
    allLocalStorageKeys.forEach(key => {
      if (key.startsWith('babblegram_') || key.startsWith('cookie_fallback_')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear sessionStorage
    const sessionStorageKeys = [
      'babblegram_page_loaded',
      'from_welcome_page',
      'babblegram_force_redirect'
    ];
    
    sessionStorageKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });
    
    // Clear all sessionStorage items that start with our prefix
    const allSessionStorageKeys = Object.keys(sessionStorage);
    allSessionStorageKeys.forEach(key => {
      if (key.startsWith('babblegram_')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Also clear any shared sequence handled keys (they start with babblegram_shared_sequence_handled_)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('babblegram_shared_sequence_handled_')) {
        localStorage.removeItem(key);
      }
    });
    
    // Restore only the daily streak after clearing everything
    if (dailyStreak !== null) {
      directCookieAccess.setCookie('babblegram_daily_streak', dailyStreak);
      console.log(`Restored daily streak: ${dailyStreak}`);
    }
    
    console.log('Game data cleared successfully, daily streak preserved');
    return true;
  } catch (error) {
    console.error('Error clearing game data:', error);
    return false;
  }
};

/**
 * Checks URL for clear parameter and clears data if present
 */
export const checkAndClearGameData = () => {
  console.log('Checking for clear parameter in URL...');
  const urlParams = new URLSearchParams(window.location.search);
  const shouldClear = urlParams.get('clear') === 'true';
  
  console.log('Clear parameter check:', { shouldClear, search: window.location.search });
  
  if (shouldClear) {
    console.log('Clear parameter detected, clearing all game data');
    const success = clearAllGameData();
    
    if (success) {
      // Show toast notification
      toast({
        title: "Game Data Cleared",
        description: "All game progress and settings have been reset.",
      });
      
      // Remove the clear parameter from URL and reload
      urlParams.delete('clear');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      
      // Show a brief message and reload
      console.log('Game data cleared, reloading page...');
      setTimeout(() => {
        window.location.reload();
      }, 1500); // Increased delay to show toast
      return true;
    }
  }
  return false;
};

// Run immediately when module loads (before React initialization)
if (typeof window !== 'undefined') {
  checkAndClearGameData();
}